import React, { useRef } from 'react';
import { QRCodeSVG } from 'qrcode.react';

/**
 * Etiqueta de identificação para impressão na Brother QL-810W
 * Dimensões: 30mm x 90mm (landscape)
 */
const EtiquetaCheckin = ({ label, onClose }) => {
  const printRef = useRef(null);

  const handlePrint = () => {
    const content = printRef.current;
    if (!content) return;

    const printWindow = window.open('', '_blank', 'width=400,height=200');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Etiqueta - ${label.child_name}</title>
        <style>
          @page {
            size: 90mm 30mm;
            margin: 0;
          }
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body {
            width: 90mm;
            height: 30mm;
            font-family: Arial, Helvetica, sans-serif;
            overflow: hidden;
          }
          .label {
            width: 90mm;
            height: 30mm;
            display: flex;
            align-items: center;
            padding: 1.5mm 2mm;
            gap: 1.5mm;
          }
          .qr-section {
            flex-shrink: 0;
            width: 22mm;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            gap: 0.5mm;
          }
          .qr-section svg {
            width: 20mm !important;
            height: 20mm !important;
          }
          .qr-label {
            font-size: 5pt;
            color: #666;
            text-align: center;
          }
          .divider {
            width: 0.3mm;
            height: 22mm;
            background: #ddd;
            flex-shrink: 0;
          }
          .info-section {
            flex: 1;
            min-width: 0;
            display: flex;
            flex-direction: column;
            justify-content: center;
            gap: 0.8mm;
          }
          .child-name {
            font-size: 12pt;
            font-weight: 900;
            color: #000;
            line-height: 1.1;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          }
          .child-id {
            font-size: 7pt;
            font-weight: 700;
            color: #333;
            letter-spacing: 0.5px;
          }
          .grupo {
            font-size: 7.5pt;
            font-weight: 700;
            color: #1A4D2E;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          }
          .meta-row {
            display: flex;
            align-items: center;
            gap: 3mm;
            font-size: 6.5pt;
            color: #555;
          }
          .meta-row span { white-space: nowrap; }
          .logos-section {
            flex-shrink: 0;
            width: 14mm;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            gap: 1mm;
          }
          .logos-section img {
            width: 12mm;
            height: auto;
            object-fit: contain;
          }
        </style>
      </head>
      <body>
        <div class="label">
          <div class="qr-section">
            ${content.querySelector('.qr-container').innerHTML}
            <div class="qr-label">Raizes</div>
          </div>
          <div class="divider"></div>
          <div class="info-section">
            <div class="child-name">${label.child_name}</div>
            <div class="child-id">${label.child_codigo || ''}</div>
            ${label.grupo_nome ? `<div class="grupo">${label.grupo_nome}</div>` : ''}
            <div class="meta-row">
              <span>${label.date}</span>
              <span>${label.checkin_time}</span>
              ${label.pontualidade ? '<span style="color:#1A4D2E;font-weight:700">Pontual</span>' : ''}
            </div>
          </div>
          <div class="logos-section">
            <img src="${window.location.origin}/logos/logo_raizes_dark.png" alt="Raizes" />
            <img src="${window.location.origin}/logos/logo_clubinho.png" alt="Clubinho" />
          </div>
        </div>
      </body>
      </html>
    `);
    printWindow.document.close();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 500);
  };

  if (!label) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden" onClick={e => e.stopPropagation()} data-testid="label-preview">
        {/* Header */}
        <div className="bg-[#1A4D2E] px-5 py-3 flex items-center justify-between">
          <h3 className="text-white font-bold text-sm">Etiqueta de Identificacao</h3>
          <button onClick={onClose} className="text-white/60 hover:text-white text-lg font-bold">&times;</button>
        </div>

        {/* Preview */}
        <div className="p-5">
          <div ref={printRef}
            className="border-2 border-dashed border-gray-300 rounded-lg p-3 flex items-center gap-2.5"
            style={{ width: '340px', height: '113px', margin: '0 auto' }}
            data-testid="label-content"
          >
            {/* QR Section */}
            <div className="qr-container flex-shrink-0 flex flex-col items-center gap-0.5">
              <QRCodeSVG value={label.qr_code} size={68} level="M" />
              <span className="text-[7px] text-gray-400 font-semibold">Raizes</span>
            </div>

            {/* Divider */}
            <div className="w-px h-20 bg-gray-200 flex-shrink-0" />

            {/* Info */}
            <div className="flex-1 min-w-0 flex flex-col justify-center gap-0.5">
              <p className="font-black text-sm text-black leading-tight truncate">{label.child_name}</p>
              <p className="text-[10px] font-bold text-gray-600">{label.child_codigo}</p>
              {label.grupo_nome && (
                <p className="text-[10px] font-bold text-[#1A4D2E] truncate">{label.grupo_nome}</p>
              )}
              <div className="flex items-center gap-2 text-[9px] text-gray-500">
                <span>{label.date}</span>
                <span>{label.checkin_time}</span>
                {label.pontualidade && <span className="text-[#1A4D2E] font-bold">Pontual</span>}
              </div>
            </div>

            {/* Logos */}
            <div className="flex-shrink-0 flex flex-col items-center justify-center gap-1 w-[52px]">
              <img src="/logos/logo_raizes_dark.png" alt="Raizes" className="w-11 h-auto object-contain" />
              <img src="/logos/logo_clubinho.png" alt="Clubinho" className="w-11 h-auto object-contain" />
            </div>
          </div>

          <p className="text-center text-[10px] text-gray-400 mt-2">
            Visualizacao da etiqueta 30mm x 90mm
          </p>
        </div>

        {/* Actions */}
        <div className="px-5 pb-5 flex gap-3">
          <button onClick={onClose} className="flex-1 py-2.5 rounded-xl text-sm text-gray-500 border border-gray-200 hover:bg-gray-50">
            Fechar
          </button>
          <button onClick={handlePrint} data-testid="print-label-btn"
            className="flex-1 py-2.5 rounded-xl text-sm font-bold text-white bg-[#1A4D2E] hover:bg-[#163f25] flex items-center justify-center gap-2">
            Imprimir Etiqueta
          </button>
        </div>
      </div>
    </div>
  );
};

export default EtiquetaCheckin;
